import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";

import { AppHeader } from "@/components/layout/AppHeader";
import { AppSidebar } from "@/components/layout/AppSidebar";
import { useAuthStore } from "@/state/authStore";
import { useUiStore } from "@/state/uiStore";

/** Rota da troca de senha — o único destino permitido enquanto ela é obrigatória. */
const CHANGE_PASSWORD_PATH = "/alterar-senha";

export function AppShell() {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const mobileSidebarOpen = useUiStore((state) => state.mobileSidebarOpen);
  const openMobileSidebar = useUiStore((state) => state.openMobileSidebar);
  const closeMobileSidebar = useUiStore((state) => state.closeMobileSidebar);

  useEffect(() => {
    closeMobileSidebar();
  }, [closeMobileSidebar, location.pathname]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closeMobileSidebar();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMobileSidebar]);

  // Primeiro acesso: a senha veio de outra pessoa e precisa ser trocada antes
  // de qualquer outra coisa. O redirect vale para todas as rotas do shell, então
  // não há como contornar digitando um endereço.
  if (user?.must_change_password && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  return (
    <div className="flex h-full flex-col bg-ink-900 text-ink-50">
      <AppHeader onOpenMobileMenu={openMobileSidebar} />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside
          className="hidden w-64 border-r border-ink-700 bg-ink-900 lg:block"
          aria-label="Menu lateral"
        >
          <AppSidebar />
        </aside>

        <main className="min-w-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>

      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu lateral">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Fechar menu lateral"
            onClick={closeMobileSidebar}
          />
          <aside className="relative z-10 h-full w-72 border-r border-ink-700 bg-ink-900">
            <div className="flex items-center justify-between border-b border-ink-700 px-3 py-2">
              <span className="text-sm font-medium text-ink-200">Navegacao</span>
              <button
                type="button"
                className="rounded border border-ink-700 bg-ink-800 p-2 text-ink-100 hover:bg-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                aria-label="Fechar menu lateral"
                onClick={closeMobileSidebar}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <AppSidebar onNavigate={closeMobileSidebar} />
          </aside>
        </div>
      )}
    </div>
  );
}
